import { useState } from 'react';
import { LayoutDashboard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';

const INTEGRATION_TYPES: { key: string; label?: string; labelKey?: string }[] = [
  // SCADA / API / FTP / SFTP / MQTT — назви протоколів, вони однакові в усіх
  // мовах, тож лишаються літералами. Перекладу потребує лише останній пункт.
  { key: 'SCADA', label: 'SCADA' },
  { key: 'API', label: 'API' },
  { key: 'FTP', label: 'FTP' },
  { key: 'SFTP', label: 'SFTP' },
  { key: 'MQTT', label: 'MQTT' },
  { key: 'manual_upload', labelKey: 'ame_type_manual' },
];

interface MeteringIntegration {
  id: string;
  integrationType: string;
  endpointUrl: string;
  protocol: string;
  authType: string;
  status: string;
}

interface Props {
  assetId: string;
  integrations?: MeteringIntegration[];
  onSaved: () => void;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  active: { bg: 'rgba(13,122,60,0.1)', color: 'var(--ok)' },
  inactive: { bg: 'rgba(107,107,112,0.1)', color: 'var(--text-muted)' },
  error: { bg: 'var(--surface-2)', color: 'var(--accent)' },
};

export default function TabMetering({ assetId, integrations = [], onSaved }: Props) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(integrations.length === 0);
  const [form, setForm] = useState({
    integrationType: '',
    endpointUrl: '',
    protocol: '',
    authType: '',
    secretRef: '',
    status: 'active',
  });

  const textMain = 'var(--text)';
  const textMuted = 'var(--text-muted)';
  const mutedText = 'var(--text-muted)';
  const inputBg = 'var(--surface-solid)';
  const inputBorder = 'var(--line-strong)';
  const sectionBg = 'var(--surface-2)';
  const sectionBorder = 'var(--line)';
  const cardBg = 'var(--surface-solid)';
  const cardBorder = 'var(--line)';
  const iconBg = 'var(--surface-2)';
  const tagBg = 'var(--surface-2)';
  const errorBg = 'color-mix(in srgb, var(--err) 12%, transparent)';
  const errorBorder = 'color-mix(in srgb, var(--err) 28%, transparent)';

  const handleAdd = async () => {
    if (!form.integrationType || !form.endpointUrl) {
      addToast(t('ame_required'), 'warning');
      return;
    }
    setSaving(true);
    setApiError(null);
    try {
      const payload: Record<string, unknown> = {
        integrationType: form.integrationType,
        endpointUrl: form.endpointUrl,
        status: form.status,
      };
      if (form.protocol) payload.protocol = form.protocol;
      if (form.authType) payload.authType = form.authType;
      if (form.secretRef) payload.secretRef = form.secretRef;

      await api.post(`/assets/${assetId}/metering-integrations`, payload);
      addToast(t('ame_added_ok'), 'success');
      setForm({ integrationType: '', endpointUrl: '', protocol: '', authType: '', secretRef: '', status: 'active' });
      setShowForm(false);
      onSaved();
    } catch (err) {
      const message = getApiErrorMessage(err, t('ame_add_failed'));
      setApiError(message);
      addToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full px-3 py-2.5 rounded-md text-sm transition-colors focus:outline-none focus:ring-1';
  const inputStyle: React.CSSProperties = {
    background: inputBg,
    border: `1px solid ${inputBorder}`,
    color: textMain,
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }}>
            <LayoutDashboard size={15} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: textMain }}>{t('ame_title')}</h3>
            <p className="text-xs" style={{ color: textMuted }}>{t('ame_sub')}</p>
          </div>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
            style={{ border: '1px solid var(--line-strong)', color: 'var(--text)' }}
          >
            <i className="ri-add-line"></i> {t('ame_add')}
          </button>
        )}
      </div>

      {/* Existing integrations */}
      {integrations.length > 0 && (
        <div className="space-y-2 mb-5">
          {integrations.map((mi) => {
            const statusStyle = STATUS_COLORS[mi.status] ?? STATUS_COLORS.inactive;
            return (
              <div key={mi.id} className="flex items-center justify-between p-4 rounded-lg" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: iconBg }}>
                    <i className="ri-wifi-line text-sm" style={{ color: textMuted }}></i>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium" style={{ color: textMain }}>{mi.integrationType}</span>
                      {mi.protocol && (
                        <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: tagBg, color: textMuted }}>{mi.protocol}</span>
                      )}
                    </div>
                    <div className="text-xs tabular-nums mt-0.5" style={{ color: textMuted }}>{mi.endpointUrl}</div>
                    {mi.authType && <div className="text-xs mt-0.5" style={{ color: textMuted }}>Auth: {mi.authType}</div>}
                  </div>
                </div>
                <span className="text-xs font-medium px-2.5 py-1 rounded-full capitalize" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                  {mi.status}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div className="p-5 rounded-xl" style={{ background: sectionBg, border: `1px solid ${sectionBorder}` }}>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold" style={{ color: textMain }}>{t('ame_new')}</h4>
            {integrations.length > 0 && (
              <button onClick={() => setShowForm(false)} className="text-xs cursor-pointer" style={{ color: textMuted }}>
                {t('common_cancel')}
              </button>
            )}
          </div>

          {apiError && (
            <div className="mb-4 px-4 py-3 rounded-lg text-sm" style={{ background: errorBg, border: `1px solid ${errorBorder}`, color: 'var(--accent)' }}>
              <i className="ri-error-warning-line mr-2"></i>{apiError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: textMuted }}>
                {t('ame_type_label')} <span style={{ color: 'var(--text-dim)' }}>*</span>
              </label>
              <select
                value={form.integrationType}
                onChange={(e) => setForm((prev) => ({ ...prev, integrationType: e.target.value }))}
                className={inputClass}
                style={inputStyle}
              >
                <option value="">{t('common_select_type')}</option>
                {INTEGRATION_TYPES.map((it) => (
                  <option key={it.key} value={it.key}>{it.labelKey ? t(it.labelKey) : it.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: textMuted }}>
                {t('ame_endpoint')} <span style={{ color: 'var(--text-dim)' }}>*</span>
              </label>
              <input
                type="text"
                value={form.endpointUrl}
                onChange={(e) => setForm((prev) => ({ ...prev, endpointUrl: e.target.value }))}
                placeholder="https://scada.example.com/api"
                className={inputClass}
                style={inputStyle}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: textMuted }}>{t('ame_protocol')}</label>
              <input
                type="text"
                value={form.protocol}
                onChange={(e) => setForm((prev) => ({ ...prev, protocol: e.target.value }))}
                placeholder={`${t('common_eg')} Modbus TCP, REST, MQTT`}
                className={inputClass}
                style={inputStyle}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: textMuted }}>{t('ame_auth')}</label>
              <input
                type="text"
                value={form.authType}
                onChange={(e) => setForm((prev) => ({ ...prev, authType: e.target.value }))}
                placeholder={`${t('common_eg')} API Key, OAuth2, Basic`}
                className={inputClass}
                style={inputStyle}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1.5" style={{ color: textMuted }}>
                {t('ame_secret_ref')}
                <span className="ml-1 font-normal" style={{ color: textMuted }}>{t('ame_secret_hint')}</span>
              </label>
              <input
                type="text"
                value={form.secretRef}
                onChange={(e) => setForm((prev) => ({ ...prev, secretRef: e.target.value }))}
                placeholder="vault://helios/asset/scada-api-key"
                className={inputClass}
                style={inputStyle}
              />
              <p className="text-xs mt-1" style={{ color: textMuted }}>
                <i className="ri-shield-keyhole-line mr-1"></i>
                {t('ame_secret_warn')}
              </p>
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={handleAdd}
              disabled={saving || !form.integrationType || !form.endpointUrl}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
              style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i> {t('ame_adding')}</>
              ) : (
                <><i className="ri-add-line"></i> {t('ame_add')}</>
              )}
            </button>
          </div>
        </div>
      )}

      {integrations.length === 0 && !showForm && (
        <div className="text-center py-8">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
            <i className="ri-wifi-off-line text-xl" style={{ color: textMuted }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>{t('ame_empty')}</p>
          <p className="text-xs mt-1" style={{ color: textMuted }}>{t('ame_empty_hint')}</p>
        </div>
      )}
    </div>
  );
}